import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Main application store using Zustand
 * Centralizes common app state like loading, errors, and UI state
 */
export const useAppStore = create(
  devtools(
    persist(
      (set, get) => ({
        // Loading states
        loading: {
          global: false,
          auth: false,
          products: false,
          orders: false,
          shop: false,
          messages: false,
        },

        // Error states
        errors: {
          global: null,
          auth: null,
          products: null,
          orders: null,
          shop: null,
          messages: null,
        },

        // UI state
        ui: {
          sidebarOpen: false,
          modalOpen: false,
          activeTab: 'home',
          theme: 'light',
          language: 'en',
        },

        // Notifications
        notifications: [],
        unreadCount: 0,

        // Actions
        setLoading: (key, value) =>
          set((state) => ({
            loading: {
              ...state.loading,
              [key]: value,
            },
          })),

        setError: (key, error) =>
          set((state) => ({
            errors: {
              ...state.errors,
              [key]: error,
            },
          })),

        clearError: (key) =>
          set((state) => ({
            errors: {
              ...state.errors,
              [key]: null,
            },
          })),

        clearAllErrors: () =>
          set((state) => ({
            errors: Object.keys(state.errors).reduce(
              (acc, key) => ({ ...acc, [key]: null }),
              {}
            ),
          })),

        setUIState: (key, value) =>
          set((state) => ({
            ui: {
              ...state.ui,
              [key]: value,
            },
          })),

        toggleSidebar: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              sidebarOpen: !state.ui.sidebarOpen,
            },
          })),

        toggleModal: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              modalOpen: !state.ui.modalOpen,
            },
          })),

        addNotification: (notification) =>
          set((state) => ({
            notifications: [
              {
                id: Date.now(),
                timestamp: new Date().toISOString(),
                read: false,
                ...notification,
              },
              ...state.notifications,
            ].slice(0, 50), // Keep only last 50 notifications
            unreadCount: state.unreadCount + 1,
          })),

        markNotificationAsRead: (id) =>
          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          })),

        markAllNotificationsAsRead: () =>
          set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
            unreadCount: 0,
          })),

        removeNotification: (id) =>
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: state.notifications.find((n) => n.id === id)?.read
              ? state.unreadCount
              : Math.max(0, state.unreadCount - 1),
          })),

        clearNotifications: () =>
          set({
            notifications: [],
            unreadCount: 0,
          }),

        // Utility functions
        isLoading: (key) => get().loading[key] || false,
        hasError: (key) => !!get().errors[key],
        getError: (key) => get().errors[key],
        getUIState: (key) => get().ui[key],

        // Reset store
        reset: () =>
          set({
            loading: {
              global: false,
              auth: false,
              products: false,
              orders: false,
              shop: false,
              messages: false,
            },
            errors: {
              global: null,
              auth: null,
              products: null,
              orders: null,
              shop: null,
              messages: null,
            },
            ui: {
              sidebarOpen: false,
              modalOpen: false,
              activeTab: 'home',
              theme: 'light',
              language: 'en',
            },
            notifications: [],
            unreadCount: 0,
          }),
      }),
      {
        name: 'app-store',
        partialize: (state) => ({
          ui: state.ui,
          notifications: state.notifications,
          unreadCount: state.unreadCount,
        }),
      }
    ),
    {
      name: 'app-store',
    }
  )
);
